/**
 * Get in touch, from hung-hola blocks/contact/view.js:
 * - Background: the Figma fill is a video (a looping stock clip of flowing blue silk at 50% under the white
 *   fade), not downloadable from Figma, so it is rebuilt as a WebGL shader in the clip's colours, drawn at a
 *   quarter of the CSS size (the clip is soft). The still gradient under it (get-in-touch.css) stays for ?still, reduced
 *   motion, no WebGL and software WebGL (no GPU: softwareWebGL()). Runs only while the block is on screen.
 * - Dots: the shared block dots, as Portfolio's (blockDots(), assets/js/dot-ripple.ts), but the push on hover: a small
 *   circle round the pointer pushes the dots out to its rim; a light sweep on click; with a mouse only, the CSS dots
 *   stay on phones and tablets, with ?still, reduced motion or without WebGL2.
 * - Fade-in of the heading, dots, form card, checklist and contacts (assets/js/fade-in.ts).
 */
import { blockDots, createProgram } from '../../assets/js/dot-ripple';
import { fadeIn, isStill } from '../../assets/js/fade-in';
import { softwareWebGL } from '../../assets/js/motion';

const SPEED = 0.5; // flow speed, matched by eye to the owner's screen recording of the Figma prototype (2026-10-19)
const LIFT = 0.4; // mix towards white: the recording's average is this much paler than the clip's frame
const RES = 0.25; // canvas pixels per CSS pixel
const MAX_WIDTH = 480; // canvas pixels
// Colours sampled from a frame of the clip (hung-hola assets/media-library/contact-bg.webp); the still gradient in get-in-touch.css uses them too
const COLORS: Record<string, string> = { deep: '#0046c4', blue: '#0060cc', mid: '#0090f3', cyan: '#06def7', lilac: '#8fadf2' };

const VERTEX = 'attribute vec2 a; void main(){ gl_Position = vec4(a, 0.0, 1.0); }';
const FRAGMENT = `
precision mediump float;
uniform vec2 uRes;
uniform float uTime;
uniform vec3 uDeep, uBlue, uMid, uCyan, uLilac;
uniform float uLift;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
	vec2 i = floor(p), f = fract(p);
	vec2 u = f * f * (3.0 - 2.0 * f);
	return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
	float v = 0.0, a = 0.5;
	for (int i = 0; i < 3; i++) { v += a * noise(p); p = p * 2.03 + vec2(1.7, 9.2); a *= 0.5; }
	return v;
}

void main() {
	// cover-fit the 16:9 clip into the canvas, like the Figma FILL
	vec2 uv = gl_FragCoord.xy / uRes;
	float ar = uRes.x / uRes.y, clip = 16.0 / 9.0;
	vec2 v = uv - 0.5;
	if (ar > clip) v.y *= clip / ar; else v.x *= ar / clip;
	vec2 p = v * vec2(1.0, 0.56) + 0.5;

	float t = uTime;
	vec2 q = vec2(fbm(p + vec2(-t * 0.6, t * 0.15)), fbm(p + vec2(5.2, 1.3) + vec2(-t * 0.4, -t * 0.2)));
	vec2 r = vec2(fbm(p + 2.2 * q + vec2(1.7, 9.2) - vec2(t * 0.9, 0.0)), fbm(p + 2.2 * q + vec2(8.3, 2.8) + vec2(0.0, t * 0.3)));
	float f = fbm(p + 2.0 * r);

	vec3 col = mix(uBlue, uMid, smoothstep(0.2, 0.5, f));
	col = mix(uDeep, col, smoothstep(0.08, 0.3, f));
	col = mix(col, uCyan, smoothstep(0.45, 0.75, q.x * 0.5 + f * 0.6));
	col = mix(col, uLilac, smoothstep(0.52, 0.78, r.y) * 0.9);
	// thin bright creases where the warped layers fold over each other
	float crease = 1.0 - smoothstep(0.0, 0.05, abs(r.x - q.y - 0.05));
	col = mix(col, uLilac + 0.12, crease * 0.3);
	gl_FragColor = vec4(mix(col, vec3(1.0), uLift), 1.0);
}`;

const rgb = (hex: string): number[] => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);

function silk(canvas: HTMLCanvasElement): void {
	const gl = canvas.getContext('webgl', { alpha: false, antialias: false, depth: false, powerPreference: 'low-power' });
	if (!gl) {
		return;
	}
	let program: WebGLProgram;
	try {
		program = createProgram(gl, VERTEX, FRAGMENT);
	} catch (e) {
		// driver error: the still gradient stays
		console.warn('[get-in-touch] WebGL background disabled:', (e as Error).message);
		return;
	}
	gl.useProgram(program);
	gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
	gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
	const a = gl.getAttribLocation(program, 'a');
	gl.enableVertexAttribArray(a);
	gl.vertexAttribPointer(a, 2, gl.FLOAT, false, 0, 0);
	const uRes = gl.getUniformLocation(program, 'uRes');
	const uTime = gl.getUniformLocation(program, 'uTime');
	gl.uniform1f(gl.getUniformLocation(program, 'uLift'), LIFT);
	Object.entries(COLORS).forEach(([name, hex]) => {
		gl.uniform3fv(gl.getUniformLocation(program, `u${name[0].toUpperCase()}${name.slice(1)}`), rgb(hex));
	});

	const resize = (): void => {
		const box = canvas.getBoundingClientRect();
		const scale = Math.min(RES, MAX_WIDTH / Math.max(box.width, 1));
		canvas.width = Math.max(1, Math.round(box.width * scale));
		canvas.height = Math.max(1, Math.round(box.height * scale));
		gl.viewport(0, 0, canvas.width, canvas.height);
		gl.uniform2f(uRes, canvas.width, canvas.height);
	};

	let time = 0;
	let last = 0;
	let frame = 0;
	const draw = (): void => {
		gl.uniform1f(uTime, time);
		gl.drawArrays(gl.TRIANGLES, 0, 3);
	};
	const tick = (now: number): void => {
		if (last) {
			time += (Math.min(now - last, 100) / 1000) * SPEED;
		}
		last = now;
		draw();
		frame = requestAnimationFrame(tick);
	};

	resize();
	draw();
	canvas.hidden = false;
	new ResizeObserver(() => {
		resize();
		draw();
	}).observe(canvas);
	new IntersectionObserver(([entry]) => {
		cancelAnimationFrame(frame);
		last = 0;
		if (entry.isIntersecting) {
			frame = requestAnimationFrame(tick);
		}
	}).observe(canvas);
}

document.querySelectorAll<HTMLElement>('[data-px-block="get-in-touch"]').forEach((el) => {
	// Its own attribute, not data-px-fade-in: the shared GSAP fade-in took the same items too and the two fought
	// (jumps, items shown then hidden). Order as seen: the heading's parts (from the WYSIWYG field), the dots, the
	// form card (beside the heading, or under the dots in one column), then the checklist and the contacts
	const inner = el.querySelector<HTMLElement>('[data-get-in-touch-fade]');
	if (inner) {
		const items = [...inner.querySelectorAll<HTMLElement>('[data-get-in-touch-fade-item]')];
		const form = inner.querySelector<HTMLElement>('.get-in-touch__form');
		fadeIn(inner, [
			...inner.querySelectorAll<HTMLElement>('.get-in-touch__heading > *, .get-in-touch__dots'),
			...(form ? [form] : []),
			...items.filter((item) => item !== form),
		]);
	}
	if (isStill()) {
		return;
	}

	const bg = el.querySelector<HTMLCanvasElement>('[data-get-in-touch-bg]');
	if (bg) {
		softwareWebGL().then((software) => software || silk(bg));
	}

	// 3 rows of 22 white 2px dots, 18px apart; the pointer counts over the whole section. The push, not the spotlight
	// (owner 2026-10-21: better in a small area, and white dots cannot get brighter)
	const dots = el.querySelector<HTMLCanvasElement>('[data-get-in-touch-dots]');
	if (dots?.parentElement) {
		blockDots(dots, el, { cols: 22, rows: 3, gapY: 18, size: 2, color: [1, 1, 1, 1], inset: 24, hover: 'push' }, dots.parentElement);
	}
});
